import { IS_DEV_UI_ENABLED } from "./devUi";

export const PRODUCT_CONFIG_COLLECTION = "product_configs";

export const PRODUCT_CALCULATION_TYPES = [
  "fabric_meter",
  "sqft_only",
  "sqft_plus_fabric",
  "quantity_rate",
  "running_length_rate",
  "manual_amount",
];

const LEGACY_PRODUCT_CALCULATION_ALIASES = {
  quantity_fixed: "quantity_rate",
  running_feet: "running_length_rate",
};

export const normalizeProductCalculationType = (value = "") => {
  const raw = String(value || "").trim();
  const aliased = LEGACY_PRODUCT_CALCULATION_ALIASES[raw] || raw;
  return PRODUCT_CALCULATION_TYPES.includes(aliased) ? aliased : "sqft_only";
};

export const ACCESSORY_PRODUCT_CALCULATION_TYPES = [
  "quantity_rate",
  "running_length_rate",
  "manual_amount",
];

export const isAccessoryCalculationType = (value = "") =>
  ACCESSORY_PRODUCT_CALCULATION_TYPES.includes(normalizeProductCalculationType(value));

export const PRODUCT_CATEGORIES = [
  "curtain",
  "blind",
  "roman_blind",
  "track_channel",
  "motor",
  "remote",
  "bracket",
  "controller",
  "accessory",
  "service",
  "freight",
  "soft_furnishing",
  "wallpaper",
  "flooring",
  "custom",
];

export const PRODUCT_CATEGORY_LABELS = {
  curtain: "Curtain",
  blind: "Blind",
  roman_blind: "Roman Blind",
  track_channel: "Track / Channel",
  motor: "Motor",
  remote: "Remote",
  bracket: "Bracket",
  controller: "Controller",
  accessory: "Accessory",
  service: "Service",
  freight: "Freight",
  soft_furnishing: "Soft Furnishing",
  wallpaper: "Wallpaper",
  flooring: "Flooring",
  custom: "Custom",
};

export const PRODUCT_CALCULATION_LABELS = {
  fabric_meter: "Fabric Meter",
  sqft_only: "Sqft Only",
  sqft_plus_fabric: "Sqft + Fabric",
  quantity_rate: "Quantity x Rate",
  running_length_rate: "Running Length x Rate",
  manual_amount: "Manual Amount",
  running_feet: "Running Feet",
  quantity_fixed: "Quantity / Fixed",
};

export const PRODUCT_DISCOUNT_SCOPES = [
  "fabric_only",
  "line_total",
  "none",
];

const normalizeProductDiscountScope = (value = "") =>
  PRODUCT_DISCOUNT_SCOPES.includes(value) ? value : "";

const DEFAULT_PRODUCT_IDS = {
  curtains: "default-curtains",
  romanNormal: "default-roman-blind-normal",
  romanClipOn: "default-roman-blind-clip-on",
  zebra: "default-zebra-blind",
  roller: "default-roller-blind",
  venetian: "default-venetian-blind",
  vertical: "default-vertical-blind",
  motor: "default-motor",
  remote: "default-remote",
  trackChannel: "default-track-channel",
  installationCharge: "default-installation-charge",
};

export const normalizeProductName = (value = "") =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const PRODUCT_NAME_ALIASES = {
  zebra: "zebra blind",
  roller: "roller blind",
  venetian: "venetian blind",
  vertical: "vertical blind",
  pvc: "pvc blind",
  roman: "roman blind normal",
  "roman normal": "roman blind normal",
  "roman clip on": "roman blind clip on",
};

const normalizeProductLookupName = (value = "") => {
  const normalized = normalizeProductName(value).replace(/\s+saved$/, "").trim();
  return PRODUCT_NAME_ALIASES[normalized] || normalized;
};

export const buildProductSlug = (value = "") =>
  normalizeProductName(value).replace(/\s+/g, "-") || "custom-product";

export const normalizeStitchingKey = (value = "") =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "";

const finiteNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const nonNegativeNumber = (value, fallback = 0) =>
  Math.max(0, finiteNumber(value, fallback));

const positiveNumberOrNull = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const round2 = (value) =>
  Math.round((finiteNumber(value, 0) + Number.EPSILON) * 100) / 100;

export const BLIND_SQFT_ROUNDING_MODES = ["exact", "ceil"];

export const normalizeBlindSqftRoundingMode = (
  value = "exact",
  fallback = "exact"
) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "ceil" || normalized === "round_up" || normalized === "roundup") {
    return "ceil";
  }
  if (normalized === "exact" || normalized === "none" || normalized === "round_2") {
    return "exact";
  }
  return BLIND_SQFT_ROUNDING_MODES.includes(fallback) ? fallback : "exact";
};

export const resolveBlindSqftRoundingMode = (
  settings = {},
  fallback = "exact"
) => {
  const booleanMode =
    settings?.roundBlindSqftUp === true
      ? "ceil"
      : settings?.roundBlindSqftUp === false
      ? "exact"
      : undefined;

  return normalizeBlindSqftRoundingMode(
    settings?.blindSqftRoundingMode ??
      settings?.calculationSettings?.blindSqftRoundingMode ??
      settings?.fabricCalcSettings?.blindSqftRoundingMode ??
      booleanMode,
    fallback
  );
};

export const calculateBlindSqft = ({
  widthIn,
  heightIn,
  roundingMode = "exact",
} = {}) => {
  const resolvedRoundingMode = normalizeBlindSqftRoundingMode(roundingMode);
  const rawSqft =
    (finiteNumber(widthIn, 0) * finiteNumber(heightIn, 0)) / 144;

  if (!Number.isFinite(rawSqft) || rawSqft <= 0) {
    return {
      rawSqft: 0,
      billableSqft: 0,
      roundingMode: resolvedRoundingMode,
    };
  }

  const roundedRawSqft = round2(rawSqft);
  const billableSqft =
    resolvedRoundingMode === "ceil" ? Math.ceil(rawSqft) : roundedRawSqft;

  return {
    rawSqft: roundedRawSqft,
    billableSqft,
    roundingMode: resolvedRoundingMode,
  };
};

export const isBlindSqftCalculationProduct = (productConfig = {}) => {
  const calculationType = normalizeProductCalculationType(
    productConfig?.calculationType
  );
  if (!["sqft_only", "sqft_plus_fabric"].includes(calculationType)) {
    return false;
  }

  const category = String(productConfig?.category || "").trim().toLowerCase();
  const productName = normalizeProductName(
    productConfig?.name ||
      productConfig?.productName ||
      productConfig?.productType ||
      productConfig?.product ||
      ""
  );

  return (
    category === "blind" ||
    category === "roman_blind" ||
    productName.includes("blind")
  );
};

export const CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION =
  "curtain-usable-width-ceil-v1";

const round4 = (value) =>
  Math.round((finiteNumber(value, 0) + Number.EPSILON) * 10000) / 10000;

export const calculateCurtainPieces = ({
  widthIn,
  fabricWidthIn,
  usableWidthDeductionIn,
  fullness,
} = {}) => {
  const safeFabricWidthIn = Math.max(0, finiteNumber(fabricWidthIn, 0));
  const safeUsableWidthDeductionIn = Math.max(
    0,
    finiteNumber(usableWidthDeductionIn, 0)
  );
  const usableFabricWidthIn = Math.max(
    1,
    safeFabricWidthIn - safeUsableWidthDeductionIn
  );
  const safeFullness = Math.max(0, finiteNumber(fullness, 1));
  const rawPiecesExact =
    (Math.max(0, finiteNumber(widthIn, 0)) * safeFullness) /
    usableFabricWidthIn;
  const pieces = rawPiecesExact > 0 ? Math.ceil(rawPiecesExact) : 0;

  return {
    fabricWidthIn: round2(safeFabricWidthIn),
    usableWidthDeductionIn: round2(safeUsableWidthDeductionIn),
    usableFabricWidthIn: round2(usableFabricWidthIn),
    fullness: round4(safeFullness),
    rawPieces: round4(rawPiecesExact),
    pieces,
    roundingMode: "ceil",
    calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
  };
};

const getStitchingStyleObjectLabel = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  return (
    value.name ||
    value.label ||
    value.title ||
    value.displayName ||
    value.stitchLabel ||
    value.stitchingLabel ||
    value.id ||
    ""
  );
};

const getStitchingCandidateValues = (row = {}) => [
  // The current select value/id is the source of truth. Older saved rows can
  // still carry stale display fields like `stitchLabel`, so those are fallbacks.
  row?.stitchingStyleId,
  row?.stitchingId,
  row?.stitchId,
  typeof row?.stitchingStyle === "string" ? row.stitchingStyle : "",
  row?.stitchingStyle?.id,
  getStitchingStyleObjectLabel(row?.stitchingStyle),
  row?.stitch,
  row?.stitchType,
  row?.stitching,
  row?.stitchingType,
  row?.stitchLabel,
  row?.stitchingLabel,
  row?.selectedStitch?.id,
  getStitchingStyleObjectLabel(row?.selectedStitch),
  row?.selectedStitching?.id,
  getStitchingStyleObjectLabel(row?.selectedStitching),
];

export const resolveSelectedStitchingStyle = (row = {}, stitchingStyles = {}) => {
  const styles =
    stitchingStyles && typeof stitchingStyles === "object" ? stitchingStyles : {};
  const entries = Object.entries(styles);
  if (!entries.length) return null;

  const candidates = getStitchingCandidateValues(row)
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .filter((value) => !["-", "n/a", "na", "undefined", "null"].includes(value.toLowerCase()));

  for (const candidate of candidates) {
    if (styles[candidate]) {
      return {
        key: candidate,
        candidate,
        candidateKey: normalizeStitchingKey(candidate),
        config: styles[candidate],
        source: "exact",
      };
    }

    const normalizedCandidate = normalizeProductName(candidate);
    const matched = entries.find(([key, config]) => {
      const labels = [
        key,
        config?.id,
        config?.name,
        config?.label,
        config?.title,
        config?.displayName,
        config?.stitchLabel,
        config?.stitchingLabel,
      ]
        .map((value) => normalizeProductName(value))
        .filter(Boolean);
      return labels.includes(normalizedCandidate);
    });

    if (matched) {
      return {
        key: matched[0],
        candidate,
        candidateKey: normalizeStitchingKey(candidate),
        config: matched[1],
        source: "normalized",
      };
    }
  }

  return null;
};

const getRowSelectedStitchFullness = (row = {}) =>
  nonNegativeNumber(
    row?.selectedStitch?.fullness ??
      row?.selectedStitching?.fullness ??
      row?.stitchingStyle?.fullness ??
      row?.stitchingFullness,
    0
  );

const getRowManualFullnessOverride = (row = {}) =>
  positiveNumberOrNull(row?.fullness ?? row?.curtainFullness);

const resolveQuoteFullnessOverrideForStitch = (
  matchedStitch = null,
  quoteFullnessOverrides = {}
) => {
  if (
    !matchedStitch ||
    !quoteFullnessOverrides ||
    typeof quoteFullnessOverrides !== "object"
  ) {
    return null;
  }

  const matchedKey = matchedStitch.key || "";
  const matchedLabel =
    matchedStitch.config?.label ||
    matchedStitch.config?.name ||
    matchedStitch.config?.displayName ||
    matchedKey;
  const rawCandidateKeys = [
    matchedStitch.candidate,
    matchedStitch.candidateKey,
    matchedStitch.config?.id,
    matchedStitch.config?.stitchingStyleId,
    matchedStitch.config?.stitchingConfigId,
    matchedStitch.config?.stitchingId,
    matchedKey,
    matchedLabel,
    matchedStitch.config?.name,
    matchedStitch.config?.label,
    matchedStitch.config?.title,
    matchedStitch.config?.displayName,
    matchedStitch.config?.stitchLabel,
    matchedStitch.config?.stitchingLabel,
  ];
  const candidateKeys = Array.from(
    new Set(
      rawCandidateKeys
        .flatMap((value) => [
          value,
          normalizeStitchingKey(value),
          normalizeProductName(value),
        ])
        .filter(Boolean)
    )
  );

  for (const candidateKey of candidateKeys) {
    const override = quoteFullnessOverrides[candidateKey];
    const quoteFullness = positiveNumberOrNull(
      override && typeof override === "object" ? override.quoteFullness : override
    );
    if (quoteFullness) {
      return {
        ...((override && typeof override === "object" ? override : {}) || {}),
        stitchingKey: matchedKey,
        stitchingLabel: matchedLabel,
        quoteFullness,
      };
    }
  }

  return null;
};

export const resolveCurtainFullness = (
  row = {},
  productConfig = null,
  stitchingStyles = {},
  quoteSettings = {}
) => {
  const config = productConfig ? normalizeProductConfig(productConfig) : null;
  const productName = normalizeProductName(
    row.productName || row.productType || row.product || config?.name || ""
  );
  const fabricRules =
    quoteSettings.fabricRules || config?.fabricRules || normalizeFabricRuleSet({}, "sqft_only");
  const isRoman =
    productName.startsWith("roman") ||
    config?.fabricRules?.meterFormulaType === "roman_panels";
  const isSqftBlind =
    config?.calculationType === "sqft_only" ||
    (config?.category === "blind" && !isRoman);
  const usesCurtainStitchingFullness =
    productName === "curtains" ||
    (config?.calculationType === "fabric_meter" &&
      config?.fabricRules?.fullnessSource === "stitching");
  const rowOverrideFullness = getRowManualFullnessOverride(row);
  const productDefaultFullness =
    fabricRules?.source === "legacy_global_settings"
      ? null
      : positiveNumberOrNull(fabricRules.fullness);
  const legacyGlobalFallback =
    fabricRules?.source === "legacy_global_settings"
      ? positiveNumberOrNull(fabricRules.fullness)
      : quoteSettings.allowLegacyGlobalFullnessFallback === true
      ? positiveNumberOrNull(
          quoteSettings.legacyGlobalFullnessFallback ?? quoteSettings.defaultFullness
        )
      : null;

  if (isSqftBlind || (fabricRules.fullnessSource === "none" && !usesCurtainStitchingFullness)) {
    return {
      fullness: 1,
      source: "not_applicable",
      applicable: false,
      rowOverride: null,
      productFullness: null,
      productDefaultFullness: null,
      stitchingFullness: null,
      legacyGlobalFallback: null,
      stitchingStyleKey: "",
      stitchingStyleConfig: null,
    };
  }

  if (rowOverrideFullness) {
    return {
      fullness: rowOverrideFullness,
      source: "row_override",
      applicable: true,
      rowOverride: rowOverrideFullness,
      productFullness: productDefaultFullness,
      productDefaultFullness,
      stitchingFullness: null,
      legacyGlobalFallback: null,
      legacyGlobalFallbackIgnored: legacyGlobalFallback,
      stitchingStyleKey: "",
      stitchingStyleConfig: null,
    };
  }

  if (isRoman || !usesCurtainStitchingFullness) {
    const fullness = productDefaultFullness || legacyGlobalFallback || 1;
    return {
      fullness,
      source: productDefaultFullness
        ? "product_default"
        : legacyGlobalFallback
        ? "legacy_global_fallback"
        : "default_one",
      applicable: true,
      rowOverride: null,
      productFullness: productDefaultFullness,
      productDefaultFullness,
      stitchingFullness: null,
      legacyGlobalFallback:
        productDefaultFullness || !legacyGlobalFallback ? null : legacyGlobalFallback,
      stitchingStyleKey: "",
      stitchingStyleConfig: null,
    };
  }

  const matchedStitch = resolveSelectedStitchingStyle(row, stitchingStyles);
  const matchedStitchFullness = nonNegativeNumber(matchedStitch?.config?.fullness, 0);
  const rowStitchFullness = getRowSelectedStitchFullness(row);
  const stitchingFullness =
    matchedStitchFullness > 0 ? matchedStitchFullness : rowStitchFullness;
  const fallbackFullness =
    productDefaultFullness || legacyGlobalFallback || 1;
  const quoteFullnessOverride = resolveQuoteFullnessOverrideForStitch(
    matchedStitch,
    quoteSettings.quoteFullnessOverrides
  );

  return {
    fullness:
      quoteFullnessOverride?.quoteFullness ||
      (stitchingFullness > 0 ? stitchingFullness : fallbackFullness),
    source:
      quoteFullnessOverride?.quoteFullness
        ? "quote_stitch_override"
        : matchedStitchFullness > 0
        ? "stitching_style"
        : rowStitchFullness > 0
        ? "row_selected_stitch"
        : productDefaultFullness
        ? "product_default"
        : legacyGlobalFallback
        ? "legacy_global_fallback"
        : "default_one",
    applicable: true,
    rowOverride: null,
    quoteFullness: quoteFullnessOverride?.quoteFullness || null,
    quoteFullnessOverride,
    productFullness: productDefaultFullness,
    productDefaultFullness,
    stitchingFullness,
    legacyGlobalFallback:
      quoteFullnessOverride?.quoteFullness ||
      stitchingFullness > 0 ||
      productDefaultFullness ||
      !legacyGlobalFallback
        ? null
        : legacyGlobalFallback,
    legacyGlobalFallbackIgnored:
      (quoteFullnessOverride?.quoteFullness || stitchingFullness > 0) &&
      legacyGlobalFallback
        ? legacyGlobalFallback
        : null,
    stitchingStyleKey: matchedStitch?.key || "",
    stitchingStyleConfig: matchedStitch?.config || null,
    stitchingLabel:
      matchedStitch?.config?.label ||
      matchedStitch?.config?.name ||
      matchedStitch?.config?.displayName ||
      matchedStitch?.key ||
      "",
  };
};

const legacyFabricDefaults = (settings = {}) => {
  const curtainTopHemIn = nonNegativeNumber(
    settings.curtain_top_hem ?? settings.curtainTopHem,
    4
  );
  const curtainBottomHemIn = nonNegativeNumber(
    settings.curtain_bottom_hem ?? settings.curtainBottomHem,
    4
  );
  const romanTopHemIn = nonNegativeNumber(
    settings.roman_top_hem ?? settings.romanTopHem,
    3
  );
  const romanBottomHemIn = nonNegativeNumber(
    settings.roman_bottom_hem ?? settings.romanBottomHem,
    4
  );
  const usableWidthDeductionIn = nonNegativeNumber(
    settings.usable_width_deduction ?? settings.usableWidthDeduction,
    4
  );

  return {
    curtainTopHemIn,
    curtainBottomHemIn,
    curtainDropAllowanceIn: round2(curtainTopHemIn + curtainBottomHemIn),
    romanTopHemIn,
    romanBottomHemIn,
    romanDropAllowanceIn: round2(romanTopHemIn + romanBottomHemIn),
    usableWidthDeductionIn,
    sideAllowancePerSideIn: round2(usableWidthDeductionIn / 2),
  };
};

const normalizeFabricRuleSet = (rules = {}, calculationType = "sqft_only") => {
  const topHemIn = nonNegativeNumber(rules.topHemIn ?? rules.topHem ?? rules.top_hem, 0);
  const bottomHemIn = nonNegativeNumber(
    rules.bottomHemIn ?? rules.bottomHem ?? rules.bottom_hem,
    0
  );
  const hasExplicitHemFields =
    rules.topHemIn != null ||
    rules.topHem != null ||
    rules.top_hem != null ||
    rules.bottomHemIn != null ||
    rules.bottomHem != null ||
    rules.bottom_hem != null;
  const dropAllowanceIn = nonNegativeNumber(
    hasExplicitHemFields ? undefined : rules.dropAllowanceIn,
    round2(topHemIn + bottomHemIn)
  );
  const usableWidthDeductionSource =
    rules.usableWidthDeductionIn ?? rules.usable_width_deduction;
  const sideAllowancePerSideIn = nonNegativeNumber(
    rules.sideAllowancePerSideIn ?? rules.sideAllowanceIn,
    usableWidthDeductionSource == null
      ? 0
      : nonNegativeNumber(usableWidthDeductionSource, 0) / 2
  );
  const usableWidthDeductionIn = round2(sideAllowancePerSideIn * 2);
  const explicitFullnessSource = String(rules.fullnessSource || "").trim();
  const normalizedCalculationType = normalizeProductCalculationType(calculationType);
  const fullnessSource = ["product", "stitching", "none"].includes(explicitFullnessSource)
    ? explicitFullnessSource
    : calculationType === "sqft_only" || isAccessoryCalculationType(normalizedCalculationType)
    ? "none"
    : "product";

  return {
    enabled:
      rules.enabled ??
      ["fabric_meter", "sqft_plus_fabric"].includes(normalizedCalculationType),
    fabricWidthIn: Math.max(1, finiteNumber(rules.fabricWidthIn, 54)),
    fullness: Math.max(0.1, finiteNumber(rules.fullness, 1)),
    fullnessSource,
    topHemIn,
    bottomHemIn,
    dropAllowanceIn,
    sideAllowancePerSideIn,
    usableWidthDeductionIn,
    widthAllowanceIn: usableWidthDeductionIn,
    panelRounding: ["ceil_full", "ceil_half", "none"].includes(
      rules.panelRounding
    )
      ? rules.panelRounding
      : "ceil_full",
    meterFormulaType: [
      "curtain_panels",
      "roman_panels",
      "simple_length",
    ].includes(rules.meterFormulaType)
      ? rules.meterFormulaType
      : "simple_length",
    repeatAware: rules.repeatAware !== false,
  };
};

const buildLegacyFabricRules = (row = {}, globalSettings = {}) => {
  const legacy = legacyFabricDefaults(globalSettings);
  const normalizedProduct = normalizeProductName(
    row.productName || row.productType || row.product || ""
  );
  const isCurtain = normalizedProduct === "curtains";
  const isRoman = normalizedProduct.startsWith("roman");

  if (isCurtain) {
    return normalizeFabricRuleSet(
      {
        enabled: true,
        fabricWidthIn: row.fabricWidthIn || 54,
        topHemIn: legacy.curtainTopHemIn,
        bottomHemIn: legacy.curtainBottomHemIn,
        dropAllowanceIn: legacy.curtainDropAllowanceIn,
        sideAllowancePerSideIn: legacy.sideAllowancePerSideIn,
        fullness: 2.63,
        panelRounding: "ceil_half",
        meterFormulaType: "curtain_panels",
        repeatAware: true,
      },
      "fabric_meter"
    );
  }

  if (isRoman) {
    return normalizeFabricRuleSet(
      {
        enabled: true,
        fabricWidthIn: row.fabricWidthIn || 54,
        topHemIn: legacy.romanTopHemIn,
        bottomHemIn: legacy.romanBottomHemIn,
        dropAllowanceIn: legacy.romanDropAllowanceIn,
        sideAllowancePerSideIn: legacy.sideAllowancePerSideIn,
        panelRounding: "ceil_full",
        meterFormulaType: "roman_panels",
        repeatAware: true,
      },
      "sqft_plus_fabric"
    );
  }

  return normalizeFabricRuleSet(
    {
      enabled: false,
      fabricWidthIn: row.fabricWidthIn || 54,
      sideAllowancePerSideIn: legacy.sideAllowancePerSideIn,
    },
    "sqft_only"
  );
};

const defaultRequiredFieldsForType = (type) => {
  const base = {
    width: true,
    height: true,
    fabric: false,
    fabricWidth: false,
    mrpPerMeter: false,
    ratePerSqft: false,
    ratePerFeet: false,
    length: false,
    ratePerLength: false,
    rate: false,
    mrp: false,
    quantity: false,
    unitRate: false,
    manualAmount: false,
    stitching: false,
    track: false,
  };

  if (type === "fabric_meter") {
    return {
      ...base,
      fabric: true,
      fabricWidth: true,
      mrpPerMeter: true,
      mrp: true,
      stitching: true,
      track: true,
    };
  }

  if (type === "sqft_plus_fabric") {
    return {
      ...base,
      fabric: true,
      fabricWidth: true,
      mrpPerMeter: true,
      mrp: true,
      ratePerSqft: true,
      rate: true,
    };
  }

  if (type === "running_length_rate" || type === "running_feet") {
    return {
      ...base,
      width: false,
      height: false,
      length: true,
      ratePerLength: true,
      ratePerFeet: true,
      rate: true,
    };
  }

  if (type === "quantity_rate" || type === "quantity_fixed") {
    return {
      ...base,
      width: false,
      height: false,
      quantity: true,
      unitRate: true,
      rate: true,
    };
  }

  if (type === "manual_amount") {
    return {
      ...base,
      width: false,
      height: false,
      manualAmount: true,
    };
  }

  return { ...base, ratePerSqft: true };
};

export const createProductConfigDraft = (overrides = {}) => {
  const calculationType = normalizeProductCalculationType(overrides.calculationType);
  const name = String(overrides.name || "New Product").trim();
  const inferredUsesFabric =
    overrides.usesFabric ??
    Boolean(
      overrides.fabricRules?.enabled ||
        ["fabric_meter", "sqft_plus_fabric"].includes(calculationType)
    );

  return normalizeProductConfig({
    id: overrides.id || `custom-${buildProductSlug(name)}-${Date.now()}`,
    name,
    category: overrides.category || "custom",
    active: overrides.active !== false,
    usedInQuoteTypes: overrides.usedInQuoteTypes || ["curtain"],
    calculationType,
    usesFabric: inferredUsesFabric,
    supportsLining: overrides.supportsLining ?? inferredUsesFabric,
    discountScope: overrides.discountScope || "",
    unitLabels: overrides.unitLabels || {},
    requiredFields: {
      ...defaultRequiredFieldsForType(calculationType),
      ...(overrides.requiredFields || {}),
    },
    fabricRules: overrides.fabricRules || {},
    sqftRules: overrides.sqftRules || {},
    runningFeetRules: overrides.runningFeetRules || {},
    quantityRules: overrides.quantityRules || {},
    stitchingRules: overrides.stitchingRules || {},
    trackRules: overrides.trackRules || {},
    displayFields: overrides.displayFields || {},
    pricingFields: overrides.pricingFields || {},
    workshopRules: overrides.workshopRules || {},
    pdfRules: overrides.pdfRules || {},
    isDefault: overrides.isDefault === true,
    createdAt: overrides.createdAt || null,
    updatedAt: overrides.updatedAt || null,
    createdBy: overrides.createdBy || "",
    updatedBy: overrides.updatedBy || "",
  });
};

const baseProductConfig = ({
  id,
  name,
  category,
  calculationType,
  discountScope,
  usesFabric,
  supportsLining,
  requiredFields = {},
  fabricRules = {},
  sqftRules = {},
  stitchingRules = {},
  trackRules = {},
  workshopRules = {},
  displayFields = {},
  pricingFields = {},
  pdfRules = {},
}) =>
  createProductConfigDraft({
    id,
    name,
    category,
    calculationType,
    discountScope,
    usesFabric,
    supportsLining,
    active: true,
    usedInQuoteTypes: ["curtain"],
    isDefault: true,
    requiredFields,
    fabricRules,
    sqftRules,
    stitchingRules,
    trackRules,
    displayFields,
    pricingFields,
    workshopRules,
    pdfRules,
  });

const baseAccessoryProductConfig = ({
  id,
  name,
  category = "accessory",
  calculationType = "quantity_rate",
  discountScope = "line_total",
  requiredFields = {},
  runningFeetRules = {},
  quantityRules = {},
  workshopRules = {},
  displayFields = {},
  pricingFields = {},
  pdfRules = {},
  unitLabels = {},
}) =>
  createProductConfigDraft({
    id,
    name,
    category,
    calculationType,
    discountScope,
    usesFabric: false,
    supportsLining: false,
    active: true,
    usedInQuoteTypes: ["curtain"],
    isDefault: true,
    unitLabels,
    requiredFields,
    runningFeetRules,
    quantityRules,
    fabricRules: { enabled: false, fullnessSource: "none" },
    sqftRules: { enabled: false },
    stitchingRules: { enabled: false },
    trackRules: { enabled: false },
    displayFields: {
      showFabric: false,
      showMrp: false,
      showRate: true,
      showStitching: false,
      showSqft: false,
      showFabricMeters: false,
      ...displayFields,
    },
    pricingFields: {
      fabricAffectsPrice: false,
      sqftAffectsPrice: false,
      rateAffectsPrice: true,
      stitchingAffectsPrice: false,
      trackAffectsPrice: false,
      ...pricingFields,
    },
    workshopRules: {
      includeInWorkshopPdf: true,
      includeCuttingDetails: false,
      includeFabricSummary: false,
      includeRollOptimizer: false,
      ...workshopRules,
    },
    pdfRules: {
      showFabric: false,
      showSqft: false,
      showRate: true,
      showMeasurements: true,
      showCalculationBreakdown: false,
      ...pdfRules,
    },
  });

export const buildDefaultProductConfigs = (legacySettings = {}) => {
  const legacy = legacyFabricDefaults(legacySettings);

  return [
  baseProductConfig({
    id: DEFAULT_PRODUCT_IDS.curtains,
    name: "Curtains",
    category: "curtain",
    calculationType: "fabric_meter",
    discountScope: "fabric_only",
    usesFabric: true,
    supportsLining: true,
    fabricRules: {
      enabled: true,
      fabricWidthIn: 54,
      fullness: 2.63,
      fullnessSource: "stitching",
      topHemIn: legacy.curtainTopHemIn,
      bottomHemIn: legacy.curtainBottomHemIn,
      dropAllowanceIn: legacy.curtainDropAllowanceIn,
      sideAllowancePerSideIn: legacy.sideAllowancePerSideIn,
      panelRounding: "ceil_half",
      meterFormulaType: "curtain_panels",
      repeatAware: true,
    },
    stitchingRules: { enabled: true, allowStitchingStyleSelection: true },
    trackRules: { enabled: true, allowTrack: true },
    workshopRules: {
      includeInWorkshopPdf: true,
      includeCuttingDetails: true,
      includeFabricSummary: true,
      includeRollOptimizer: true,
    },
    displayFields: {
      fabricLabel: "Fabric",
      showFabric: true,
      showMrp: true,
      showRate: false,
      showStitching: true,
      showSqft: false,
      showFabricMeters: true,
    },
    pricingFields: {
      fabricAffectsPrice: true,
      sqftAffectsPrice: false,
      rateAffectsPrice: false,
      stitchingAffectsPrice: true,
      trackAffectsPrice: true,
    },
  }),
  baseProductConfig({
    id: DEFAULT_PRODUCT_IDS.romanNormal,
    name: "Roman Blind - Normal",
    category: "blind",
    calculationType: "sqft_plus_fabric",
    discountScope: "fabric_only",
    usesFabric: true,
    supportsLining: true,
    fabricRules: {
      enabled: true,
      fabricWidthIn: 54,
      fullness: 1,
      fullnessSource: "product",
      topHemIn: legacy.romanTopHemIn,
      bottomHemIn: legacy.romanBottomHemIn,
      dropAllowanceIn: legacy.romanDropAllowanceIn,
      sideAllowancePerSideIn: legacy.sideAllowancePerSideIn,
      panelRounding: "ceil_full",
      meterFormulaType: "roman_panels",
      repeatAware: true,
    },
    sqftRules: { enabled: true, minSqft: 12, sqftFormula: "width_height_div_144" },
    workshopRules: {
      includeInWorkshopPdf: true,
      includeCuttingDetails: true,
      includeFabricSummary: true,
      includeRollOptimizer: true,
    },
    displayFields: {
      fabricLabel: "Fabric",
      showFabric: true,
      showMrp: true,
      showRate: true,
      showStitching: false,
      showSqft: true,
      showFabricMeters: true,
    },
    pricingFields: {
      fabricAffectsPrice: true,
      sqftAffectsPrice: true,
      rateAffectsPrice: true,
      stitchingAffectsPrice: false,
      trackAffectsPrice: false,
    },
  }),
  baseProductConfig({
    id: DEFAULT_PRODUCT_IDS.romanClipOn,
    name: "Roman Blind - Clip On",
    category: "blind",
    calculationType: "sqft_plus_fabric",
    discountScope: "fabric_only",
    usesFabric: true,
    supportsLining: true,
    fabricRules: {
      enabled: true,
      fabricWidthIn: 54,
      fullness: 1,
      fullnessSource: "product",
      topHemIn: legacy.romanTopHemIn,
      bottomHemIn: legacy.romanBottomHemIn,
      dropAllowanceIn: legacy.romanDropAllowanceIn,
      sideAllowancePerSideIn: legacy.sideAllowancePerSideIn,
      panelRounding: "ceil_full",
      meterFormulaType: "roman_panels",
      repeatAware: true,
    },
    sqftRules: { enabled: true, minSqft: 12, sqftFormula: "width_height_div_144" },
    workshopRules: {
      includeInWorkshopPdf: true,
      includeCuttingDetails: true,
      includeFabricSummary: true,
      includeRollOptimizer: true,
    },
    displayFields: {
      fabricLabel: "Fabric",
      showFabric: true,
      showMrp: true,
      showRate: true,
      showStitching: false,
      showSqft: true,
      showFabricMeters: true,
    },
    pricingFields: {
      fabricAffectsPrice: true,
      sqftAffectsPrice: true,
      rateAffectsPrice: true,
      stitchingAffectsPrice: false,
      trackAffectsPrice: false,
    },
  }),
  baseProductConfig({
    id: DEFAULT_PRODUCT_IDS.zebra,
    name: "Zebra Blind",
    category: "blind",
    calculationType: "sqft_only",
    discountScope: "line_total",
    usesFabric: false,
    supportsLining: false,
    sqftRules: { enabled: true, minSqft: 10, sqftFormula: "width_height_div_144" },
    displayFields: {
      fabricLabel: "Fabric / Material / Color",
      showFabric: true,
      showMrp: false,
      showRate: true,
      showStitching: false,
      showSqft: true,
      showFabricMeters: false,
    },
    pricingFields: {
      fabricAffectsPrice: false,
      sqftAffectsPrice: true,
      rateAffectsPrice: true,
      stitchingAffectsPrice: false,
      trackAffectsPrice: false,
    },
    workshopRules: {
      includeInWorkshopPdf: true,
      includeCuttingDetails: false,
      includeFabricSummary: false,
      includeRollOptimizer: false,
    },
  }),
  baseProductConfig({
    id: DEFAULT_PRODUCT_IDS.roller,
    name: "Roller Blind",
    category: "blind",
    calculationType: "sqft_only",
    discountScope: "line_total",
    usesFabric: false,
    supportsLining: false,
    sqftRules: { enabled: true, minSqft: 10, sqftFormula: "width_height_div_144" },
    displayFields: {
      fabricLabel: "Fabric / Material / Color",
      showFabric: true,
      showMrp: false,
      showRate: true,
      showStitching: false,
      showSqft: true,
      showFabricMeters: false,
    },
    pricingFields: {
      fabricAffectsPrice: false,
      sqftAffectsPrice: true,
      rateAffectsPrice: true,
      stitchingAffectsPrice: false,
      trackAffectsPrice: false,
    },
    workshopRules: {
      includeInWorkshopPdf: true,
      includeCuttingDetails: false,
      includeFabricSummary: false,
      includeRollOptimizer: false,
    },
  }),
  baseProductConfig({
    id: DEFAULT_PRODUCT_IDS.venetian,
    name: "Venetian Blind",
    category: "blind",
    calculationType: "sqft_only",
    discountScope: "line_total",
    usesFabric: false,
    supportsLining: false,
    sqftRules: { enabled: true, minSqft: 10, sqftFormula: "width_height_div_144" },
    displayFields: {
      fabricLabel: "Fabric / Material / Color",
      showFabric: true,
      showMrp: false,
      showRate: true,
      showStitching: false,
      showSqft: true,
      showFabricMeters: false,
    },
    pricingFields: {
      fabricAffectsPrice: false,
      sqftAffectsPrice: true,
      rateAffectsPrice: true,
      stitchingAffectsPrice: false,
      trackAffectsPrice: false,
    },
    workshopRules: {
      includeInWorkshopPdf: true,
      includeCuttingDetails: false,
      includeFabricSummary: false,
      includeRollOptimizer: false,
    },
  }),
  baseProductConfig({
    id: DEFAULT_PRODUCT_IDS.vertical,
    name: "Vertical Blind",
    category: "blind",
    calculationType: "sqft_only",
    discountScope: "line_total",
    usesFabric: false,
    supportsLining: false,
    sqftRules: { enabled: true, minSqft: 10, sqftFormula: "width_height_div_144" },
    displayFields: {
      fabricLabel: "Fabric / Material / Color",
      showFabric: true,
      showMrp: false,
      showRate: true,
      showStitching: false,
      showSqft: true,
      showFabricMeters: false,
    },
    pricingFields: {
      fabricAffectsPrice: false,
      sqftAffectsPrice: true,
      rateAffectsPrice: true,
      stitchingAffectsPrice: false,
      trackAffectsPrice: false,
    },
    workshopRules: {
      includeInWorkshopPdf: true,
      includeCuttingDetails: false,
      includeFabricSummary: false,
      includeRollOptimizer: false,
    },
  }),
  baseAccessoryProductConfig({
    id: DEFAULT_PRODUCT_IDS.motor,
    name: "Motor",
    category: "motor",
    calculationType: "quantity_rate",
    discountScope: "line_total",
    unitLabels: { rate: "Rs./pcs", quantity: "pcs" },
    quantityRules: { enabled: true, defaultRate: 0, defaultUnit: "pcs" },
  }),
  baseAccessoryProductConfig({
    id: DEFAULT_PRODUCT_IDS.remote,
    name: "Remote",
    category: "remote",
    calculationType: "quantity_rate",
    discountScope: "line_total",
    unitLabels: { rate: "Rs./pcs", quantity: "pcs" },
    quantityRules: { enabled: true, defaultRate: 0, defaultUnit: "pcs" },
  }),
  baseAccessoryProductConfig({
    id: DEFAULT_PRODUCT_IDS.trackChannel,
    name: "Track / Channel",
    category: "track_channel",
    calculationType: "running_length_rate",
    discountScope: "line_total",
    unitLabels: { rate: "Rs./ft", length: "ft" },
    runningFeetRules: {
      enabled: true,
      formula: "manual_feet",
      defaultRate: 0,
      defaultUnit: "ft",
    },
  }),
  baseAccessoryProductConfig({
    id: DEFAULT_PRODUCT_IDS.installationCharge,
    name: "Installation Charge",
    category: "service",
    calculationType: "manual_amount",
    discountScope: "line_total",
    workshopRules: { includeInWorkshopPdf: false },
  }),
  ];
};

const normalizeProductCategoryValue = (value = "") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return PRODUCT_CATEGORIES.includes(normalized) ? normalized : "custom";
};

export const getRowDiscountScope = (
  row = {},
  productConfig = null,
  calculationContext = {}
) => {
  const calculationType = normalizeProductCalculationType(
    productConfig?.calculationType || row?.calculationType || "sqft_only"
  );
  const category = normalizeProductCategoryValue(
    productConfig?.category || row?.category || ""
  );
  const productName = normalizeProductName(
    productConfig?.normalizedName ||
      productConfig?.name ||
      row?.productName ||
      row?.productType ||
      row?.product ||
      row?.name ||
      ""
  );
  const explicitScope = normalizeProductDiscountScope(
    row?.discountScope ||
      productConfig?.discountScope ||
      productConfig?.pricingFields?.discountScope
  );
  const discountDisabled =
    row?.discountAllowed === false ||
    productConfig?.discountAllowed === false ||
    productConfig?.pricingFields?.discountAllowed === false;

  if (discountDisabled || explicitScope === "none") return "none";
  if (explicitScope === "line_total") return "line_total";

  const isRoman =
    category === "roman_blind" ||
    productName.startsWith("roman blind") ||
    productName.startsWith("roman");
  const hasFabricMeterComponent =
    calculationContext.hasFabricMeterComponent ??
    Boolean(
      productConfig?.usesFabric === true ||
        productConfig?.fabricRules?.enabled === true ||
        ["fabric_meter", "sqft_plus_fabric"].includes(calculationType) ||
        category === "curtain" ||
        isRoman
    );

  if (explicitScope === "fabric_only") {
    return hasFabricMeterComponent ? "fabric_only" : "line_total";
  }

  if (hasFabricMeterComponent) return "fabric_only";

  return "line_total";
};

export const DEFAULT_PRODUCT_CONFIGS = buildDefaultProductConfigs();

export function normalizeProductConfig(config = {}) {
  const calculationType = normalizeProductCalculationType(config.calculationType);
  const name = String(config.name || config.productName || "Custom Product").trim();
  const normalizedName = normalizeProductName(config.normalizedName || name);
  const normalizedId = String(config.id || config.productId || buildProductSlug(name)).trim();
  const category = normalizeProductCategoryValue(config.category);
  const isAccessoryProduct = isAccessoryCalculationType(calculationType);
  const isBlindProduct =
    category === "blind" || /\bblind\b/.test(normalizedName || "");
  const isRomanBlindProduct = normalizedName.startsWith("roman blind");
  const isVenetianBlindProduct = normalizedName.includes("venetian blind");
  const isSqftOnlyBlindProduct =
    isBlindProduct &&
    !isRomanBlindProduct &&
    calculationType === "sqft_only";
  const defaultRequiredFields = defaultRequiredFieldsForType(calculationType);
  const inputRequiredFields = config.requiredFields || {};
  const normalizedRequiredFields = {
    ...defaultRequiredFields,
    ...inputRequiredFields,
  };
  normalizedRequiredFields.rate =
    inputRequiredFields.rate ??
    Boolean(
      normalizedRequiredFields.ratePerSqft ||
        normalizedRequiredFields.ratePerFeet ||
        normalizedRequiredFields.ratePerLength ||
        normalizedRequiredFields.unitRate ||
        calculationType === "quantity_rate" ||
        calculationType === "running_length_rate" ||
        calculationType === "manual_amount"
    );
  normalizedRequiredFields.mrp =
    inputRequiredFields.mrp ?? normalizedRequiredFields.mrpPerMeter;
  const normalizedFabricRules = normalizeFabricRuleSet(config.fabricRules || {}, calculationType);
  const displaySource = config.displayFields || {};
  const pricingSource = config.pricingFields || {};
  const defaultFabricLabel = isVenetianBlindProduct
    ? "Material / Color Code"
    : isSqftOnlyBlindProduct
    ? "Fabric / Material Code"
    : "Fabric";
  const incomingFabricLabel = String(displaySource.fabricLabel || "").trim();
  const fabricLabel =
    isSqftOnlyBlindProduct && (!incomingFabricLabel || incomingFabricLabel === "Fabric")
      ? defaultFabricLabel
      : incomingFabricLabel || defaultFabricLabel;
  const showFabricDefault =
    isBlindProduct
      ? true
      : displaySource.showFabric ??
    inputRequiredFields.fabric ??
    config.pdfRules?.showFabric ??
    config.workshopRules?.includeFabricSummary ??
    normalizedFabricRules.enabled;
  const includeFabricSummaryDefault =
    config.workshopRules?.includeFabricSummary ?? (!isSqftOnlyBlindProduct && !isAccessoryProduct);
  const usesFabricDefault = Boolean(
    config.usesFabric === true ||
      normalizedFabricRules.enabled ||
      ["fabric_meter", "sqft_plus_fabric"].includes(calculationType) ||
      category === "curtain" ||
      isRomanBlindProduct
  );
  const supportsLiningDefault = Boolean(
    config.supportsLining === true ||
      usesFabricDefault ||
      category === "curtain" ||
      isRomanBlindProduct
  );
  const discountScope = getRowDiscountScope(
    {},
    {
      ...config,
      category,
      calculationType,
      normalizedName,
      usesFabric: usesFabricDefault,
      fabricRules: normalizedFabricRules,
    },
    {
      hasFabricMeterComponent: usesFabricDefault,
    }
  );
  const rateAmountFieldDefault = Boolean(
    normalizedRequiredFields.rate ||
      normalizedRequiredFields.ratePerSqft ||
      normalizedRequiredFields.ratePerFeet ||
      normalizedRequiredFields.ratePerLength ||
      normalizedRequiredFields.quantity ||
      normalizedRequiredFields.unitRate ||
      normalizedRequiredFields.manualAmount
  );
  const showRateDefault =
    calculationType === "fabric_meter" && pricingSource.rateAffectsPrice !== true
      ? false
      : displaySource.showRate ?? rateAmountFieldDefault;
  const isLegacyDefaultSqftBlindWorkshopOptOut =
    isSqftOnlyBlindProduct &&
    [
      DEFAULT_PRODUCT_IDS.zebra,
      DEFAULT_PRODUCT_IDS.roller,
      DEFAULT_PRODUCT_IDS.venetian,
      DEFAULT_PRODUCT_IDS.vertical,
    ].includes(normalizedId) &&
    config.workshopRules?.includeInWorkshopPdf === false;

  return {
    id: normalizedId,
    name,
    normalizedName,
    category,
    active: config.active !== false,
    usedInQuoteTypes: Array.isArray(config.usedInQuoteTypes)
      ? config.usedInQuoteTypes
      : ["curtain"],
    calculationType,
    usesFabric: usesFabricDefault,
    supportsLining: supportsLiningDefault,
    discountScope,
    unitLabels: {
      width: config.unitLabels?.width || "in",
      height: config.unitLabels?.height || "in",
      rate:
        config.unitLabels?.rate ||
        (calculationType === "running_length_rate"
          ? "Rs./ft"
          : calculationType === "quantity_rate"
          ? "Rs./piece"
          : calculationType === "fabric_meter"
          ? "Rs./m"
          : "Rs./sqft"),
    },
    requiredFields: normalizedRequiredFields,
    fabricRules: normalizedFabricRules,
    sqftRules: {
      enabled:
        config.sqftRules?.enabled ??
        ["sqft_only", "sqft_plus_fabric"].includes(calculationType),
      minSqft: nonNegativeNumber(config.sqftRules?.minSqft, 0),
      sqftFormula: config.sqftRules?.sqftFormula || "width_height_div_144",
      rounding: ["none", "ceil", "round_2"].includes(config.sqftRules?.rounding)
        ? config.sqftRules.rounding
        : "none",
      rateSource: ["row_rate", "default_rate"].includes(config.sqftRules?.rateSource)
        ? config.sqftRules.rateSource
        : "row_rate",
      defaultRate: nonNegativeNumber(config.sqftRules?.defaultRate, 0),
    },
    runningFeetRules: {
      enabled: config.runningFeetRules?.enabled ?? calculationType === "running_length_rate",
      formula: ["width_div_12", "manual_feet"].includes(config.runningFeetRules?.formula)
        ? config.runningFeetRules.formula
        : "width_div_12",
      defaultRate: nonNegativeNumber(config.runningFeetRules?.defaultRate, 0),
      defaultUnit: config.runningFeetRules?.defaultUnit || config.unitLabels?.length || "ft",
    },
    quantityRules: {
      enabled: config.quantityRules?.enabled ?? calculationType === "quantity_rate",
      defaultRate: nonNegativeNumber(config.quantityRules?.defaultRate, 0),
      defaultUnit: config.quantityRules?.defaultUnit || config.unitLabels?.quantity || "pcs",
    },
    stitchingRules: {
      enabled: config.stitchingRules?.enabled === true,
      allowStitchingStyleSelection:
        config.stitchingRules?.allowStitchingStyleSelection === true,
      defaultStitchingStyleId: config.stitchingRules?.defaultStitchingStyleId || null,
    },
    trackRules: {
      enabled: config.trackRules?.enabled === true,
      allowTrack: config.trackRules?.allowTrack === true,
      defaultTrackLengthFt: nonNegativeNumber(config.trackRules?.defaultTrackLengthFt, 0),
    },
    displayFields: {
      fabricLabel,
      showFabric: Boolean(showFabricDefault),
      fabricDisabled: displaySource.fabricDisabled === true,
      showMrp:
        displaySource.showMrp ??
        Boolean(normalizedRequiredFields.mrpPerMeter || normalizedRequiredFields.mrp),
      showRate: showRateDefault,
      showStitching: displaySource.showStitching ?? normalizedRequiredFields.stitching,
      showSqft:
        displaySource.showSqft ??
        ["sqft_only", "sqft_plus_fabric"].includes(calculationType),
      showFabricMeters:
        displaySource.showFabricMeters ??
        normalizedFabricRules.enabled,
    },
    pricingFields: {
      fabricAffectsPrice:
        pricingSource.fabricAffectsPrice ??
        Boolean(
          normalizedFabricRules.enabled &&
            ["fabric_meter", "sqft_plus_fabric"].includes(calculationType)
        ),
      sqftAffectsPrice:
        pricingSource.sqftAffectsPrice ??
        ["sqft_only", "sqft_plus_fabric"].includes(calculationType),
      rateAffectsPrice:
        pricingSource.rateAffectsPrice ??
        ["sqft_only", "running_length_rate", "quantity_rate", "manual_amount"].includes(
          calculationType
        ),
      stitchingAffectsPrice:
        pricingSource.stitchingAffectsPrice ??
        (config.stitchingRules?.enabled === true),
      trackAffectsPrice:
        pricingSource.trackAffectsPrice ??
        (config.trackRules?.enabled === true && config.trackRules?.allowTrack === true),
    },
    workshopRules: {
      includeInWorkshopPdf:
        isLegacyDefaultSqftBlindWorkshopOptOut ||
        config.workshopRules?.includeInWorkshopPdf !== false,
      includeCuttingDetails: config.workshopRules?.includeCuttingDetails !== false,
      includeFabricSummary: isAccessoryProduct ? false : Boolean(includeFabricSummaryDefault),
      includeRollOptimizer: isAccessoryProduct
        ? false
        : config.workshopRules?.includeRollOptimizer === true,
    },
    pdfRules: {
      showFabric: config.pdfRules?.showFabric !== false,
      showSqft: config.pdfRules?.showSqft !== false,
      showRate: config.pdfRules?.showRate !== false,
      showMeasurements: config.pdfRules?.showMeasurements !== false,
      showCalculationBreakdown: config.pdfRules?.showCalculationBreakdown === true,
    },
    isDefault: config.isDefault === true,
    createdAt: config.createdAt || null,
    updatedAt: config.updatedAt || null,
    createdBy: config.createdBy || "",
    updatedBy: config.updatedBy || "",
  };
}

export const isAccessoryProductConfig = (config = {}) => {
  const normalized = normalizeProductConfig(config || {});
  return isAccessoryCalculationType(normalized.calculationType);
};

export const productSupportsLining = (productConfig = null, row = {}) => {
  const config = productConfig ? normalizeProductConfig(productConfig) : null;
  const calculationType =
    config?.calculationType ||
    normalizeProductCalculationType(row?.calculationType || "sqft_only");
  const category = String(config?.category ?? row?.category ?? "").trim().toLowerCase();
  const productName = normalizeProductName(
    config?.normalizedName ||
      config?.name ||
      row?.productName ||
      row?.productType ||
      row?.product ||
      row?.name ||
      ""
  );
  const usesFabric =
    config?.usesFabric === true ||
    (!config &&
      Boolean(
        row?.usesFabric === true ||
          row?.fabricRules?.enabled ||
          row?.isCurtain === true ||
          row?.isRoman === true
      ));
  const supportsLining =
    config?.supportsLining === true || (!config && row?.supportsLining === true);
  const isCurtainLike =
    category === "curtain" ||
    productName === "curtains" ||
    productName.includes("curtain");
  const isRomanLike =
    row?.isRoman === true ||
    category === "roman blind" ||
    productName.startsWith("roman blind") ||
    productName.includes("roman blind");

  return Boolean(
    usesFabric ||
      supportsLining ||
      calculationType === "fabric_meter" ||
      calculationType === "sqft_plus_fabric" ||
      isCurtainLike ||
      isRomanLike
  );
};

export const validateProductConfig = (config = {}, existingConfigs = []) => {
  const normalized = normalizeProductConfig(config);
  const errors = [];

  if (!normalized.name) errors.push("Product name is required.");
  if (!PRODUCT_CALCULATION_TYPES.includes(normalized.calculationType)) {
    errors.push("Calculation type is required.");
  }
  if (normalized.fabricRules.enabled && normalized.fabricRules.fabricWidthIn <= 0) {
    errors.push("Fabric width must be greater than 0.");
  }
  if (normalized.fabricRules.enabled && normalized.fabricRules.fullness <= 0) {
    errors.push("Fullness must be greater than 0.");
  }
  if (normalized.sqftRules.minSqft < 0) errors.push("Minimum sqft cannot be negative.");

  const duplicate = (Array.isArray(existingConfigs) ? existingConfigs : []).find(
    (item) =>
      item.active !== false &&
      String(item.id || "") !== String(normalized.id || "") &&
      normalizeProductName(item.normalizedName || item.name) === normalized.normalizedName
  );
  if (duplicate) errors.push("An active product with this name already exists.");

  return { valid: errors.length === 0, errors, normalized };
};

export const getDefaultProductConfigMap = (legacySettings = {}) => {
  const map = new Map();
  buildDefaultProductConfigs(legacySettings).forEach((config) => {
    const normalized = normalizeProductConfig(config);
    map.set(normalized.id, normalized);
    map.set(normalized.normalizedName, normalized);
  });
  return map;
};

export const mergeProductConfigsWithDefaults = (configs = [], legacySettings = {}) => {
  const byId = new Map();
  buildDefaultProductConfigs(legacySettings).forEach((config) => {
    const normalized = normalizeProductConfig(config);
    byId.set(normalized.id, normalized);
  });
  (Array.isArray(configs) ? configs : []).forEach((config) => {
    const normalized = normalizeProductConfig(config);
    byId.set(normalized.id, normalized);
  });
  return Array.from(byId.values());
};

export const resolveProductConfig = (row = {}, productConfigs = [], legacySettings = {}) => {
  if (row.productConfigSnapshot) {
    return normalizeProductConfig(row.productConfigSnapshot);
  }

  const configs = mergeProductConfigsWithDefaults(productConfigs, legacySettings);
  const byId = new Map(configs.map((config) => [String(config.id || ""), config]));
  const byName = new Map(
    configs.map((config) => [normalizeProductLookupName(config.normalizedName || config.name), config])
  );

  const productId = String(row.productId || "").trim();
  if (productId && byId.has(productId)) return byId.get(productId);

  const productName = row.productName || row.productType || row.product || "";
  const normalizedName = normalizeProductLookupName(productName);
  return byName.get(normalizedName) || null;
};

export const shouldUseLegacyProductCalculation = (row = {}, productConfig = null) => {
  if (row.productConfigSnapshot || row.productId) return false;
  return true;
};

export const buildProductConfigSnapshot = (productConfig = null) => {
  if (!productConfig) return null;
  const normalized = normalizeProductConfig(productConfig);
  return {
    id: normalized.id,
    name: normalized.name,
    normalizedName: normalized.normalizedName,
    category: normalized.category,
    calculationType: normalized.calculationType,
    usesFabric: normalized.usesFabric,
    supportsLining: normalized.supportsLining,
    requiredFields: normalized.requiredFields,
    fabricRules: normalized.fabricRules,
    sqftRules: normalized.sqftRules,
    runningFeetRules: normalized.runningFeetRules,
    quantityRules: normalized.quantityRules,
    stitchingRules: normalized.stitchingRules,
    trackRules: normalized.trackRules,
    displayFields: normalized.displayFields,
    pricingFields: normalized.pricingFields,
    workshopRules: normalized.workshopRules,
    pdfRules: normalized.pdfRules,
    isDefault: normalized.isDefault,
    snapshotAt: new Date().toISOString(),
  };
};

export const resolveFabricRulesForRow = (
  row = {},
  productConfig = null,
  globalSettings = {}
) => {
  const snapshotRules = row.productConfigSnapshot?.fabricRules;
  const normalizedConfig = productConfig ? normalizeProductConfig(productConfig) : null;
  const baseRules = snapshotRules
    ? normalizeFabricRuleSet(snapshotRules, row.productConfigSnapshot?.calculationType)
    : normalizedConfig?.fabricRules
    ? normalizedConfig.fabricRules
    : buildLegacyFabricRules(row, globalSettings);
  const source = snapshotRules
    ? "row_product_config_snapshot"
    : normalizedConfig?.fabricRules
    ? "product_config"
    : "legacy_global_settings";
  const fabricWidthIn = Math.max(
    1,
    nonNegativeNumber(row.fabricWidthIn, baseRules.fabricWidthIn)
  );
  const sideAllowancePerSideIn = nonNegativeNumber(
    baseRules.sideAllowancePerSideIn,
    0
  );
  const usableWidthDeductionIn = round2(sideAllowancePerSideIn * 2);
  const usableFabricWidthIn = Math.max(
    1,
    round2(fabricWidthIn - usableWidthDeductionIn)
  );

  return {
    ...baseRules,
    source,
    fabricWidthIn,
    sideAllowancePerSideIn,
    usableWidthDeductionIn,
    usableFabricWidthIn,
    totalSideAllowanceIn: usableWidthDeductionIn,
    dropAllowanceIn: nonNegativeNumber(
      baseRules.dropAllowanceIn,
      round2(
        nonNegativeNumber(baseRules.topHemIn, 0) +
          nonNegativeNumber(baseRules.bottomHemIn, 0)
      )
    ),
  };
};

export const resolveFullness = (
  row = {},
  productConfig = null,
  stitchingStyleConfig = {},
  fabricRulesOverride = null
) => {
  const config = productConfig ? normalizeProductConfig(productConfig) : null;
  const rules = fabricRulesOverride || config?.fabricRules || normalizeFabricRuleSet({}, "sqft_only");
  const source = rules.fullnessSource || "product";
  const productFullness = Math.max(0.1, nonNegativeNumber(rules.fullness, 1));
  const stitchingFullness = nonNegativeNumber(
    stitchingStyleConfig?.fullness ?? row?.stitchingFullness,
    0
  );

  if (source === "stitching") {
    return {
      fullness: stitchingFullness > 0 ? stitchingFullness : productFullness,
      source: stitchingFullness > 0 ? "stitching" : "product_fallback",
    };
  }

  if (source === "none") {
    return { fullness: 1, source: "none" };
  }

  return { fullness: productFullness, source: "product" };
};

const roundByRule = (value, rule = "none") => {
  if (rule === "ceil") return Math.ceil(finiteNumber(value, 0));
  if (rule === "round_2") return round2(value);
  return finiteNumber(value, 0);
};

const normalizeFabricKey = (value = "") =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");

const resolveProductRowFabricKey = (row = {}) => {
  const candidates = [
    row.fabric,
    row.fabricName,
    row.fabricLabel,
    row.selectedFabric,
    row.fabricDetails,
    row.material,
    row.materialCode,
    row.materialName,
    row.colorCode,
    row.itemFabric,
    row.fabricCode,
    row.fabricSku,
    row.fabricKey,
  ];

  for (const candidate of candidates) {
    if (candidate == null) continue;
    if (typeof candidate === "object") {
      const objectText =
        candidate.name ||
        candidate.label ||
        candidate.displayName ||
        candidate.fabricName ||
        candidate.fabricLabel ||
        candidate.materialName ||
        candidate.code ||
        candidate.sku ||
        candidate.itemCode ||
        "";
      const normalizedObjectKey = normalizeFabricKey(objectText);
      if (normalizedObjectKey) return normalizedObjectKey;
      continue;
    }
    const normalizedKey = normalizeFabricKey(candidate);
    if (normalizedKey) return normalizedKey;
  }

  return "";
};

const resolveProductRepeatSettings = (row = {}, quoteSettings = {}) => {
  const rowRepeat = row.repeatSettings && typeof row.repeatSettings === "object"
    ? row.repeatSettings
    : null;
  const repeatMap =
    quoteSettings.repeatSettings && typeof quoteSettings.repeatSettings === "object"
      ? quoteSettings.repeatSettings
      : {};
  const fabricKey = resolveProductRowFabricKey(row);
  const groupRepeat = fabricKey ? repeatMap[fabricKey] || null : null;
  const hasRepeatValue = (repeat = {}) =>
    Boolean(
      repeat &&
        typeof repeat === "object" &&
        (repeat.verticalEnabled === true ||
          repeat.verticalEnabled === "true" ||
          repeat.verticalRepeatEnabled === true ||
          repeat.verticalRepeatEnabled === "true" ||
          repeat.horizontalEnabled === true ||
          repeat.horizontalEnabled === "true" ||
          repeat.horizontalRepeatEnabled === true ||
          repeat.horizontalRepeatEnabled === "true" ||
          nonNegativeNumber(
            repeat.verticalRepeatInch ?? repeat.verticalRepeatIn ?? repeat.verticalRepeat,
            0
          ) > 0 ||
          nonNegativeNumber(
            repeat.horizontalRepeatInch ?? repeat.horizontalRepeatIn ?? repeat.horizontalRepeat,
            0
          ) > 0)
    );
  const repeat = hasRepeatValue(groupRepeat)
    ? groupRepeat
    : hasRepeatValue(rowRepeat)
    ? rowRepeat
    : groupRepeat || rowRepeat || {};
  return {
    verticalEnabled:
      repeat.verticalEnabled === true ||
      repeat.verticalEnabled === "true" ||
      repeat.verticalRepeatEnabled === true ||
      repeat.verticalRepeatEnabled === "true",
    verticalRepeatInch: nonNegativeNumber(
      repeat.verticalRepeatInch ??
        repeat.verticalRepeatIn ??
        repeat.verticalRepeat,
      0
    ),
    horizontalEnabled:
      repeat.horizontalEnabled === true ||
      repeat.horizontalEnabled === "true" ||
      repeat.horizontalRepeatEnabled === true ||
      repeat.horizontalRepeatEnabled === "true",
    horizontalRepeatInch: nonNegativeNumber(
      repeat.horizontalRepeatInch ??
        repeat.horizontalRepeatIn ??
        repeat.horizontalRepeat,
      0
    ),
    fabricKey,
  };
};

const applyVerticalRepeatToCutHeight = (cutHeightIn = 0, repeat = {}) => {
  const baseCutHeight = nonNegativeNumber(cutHeightIn, 0);
  const repeatIn = nonNegativeNumber(repeat.verticalRepeatInch, 0);
  if (!repeat.verticalEnabled || repeatIn <= 0 || baseCutHeight <= 0) {
    return baseCutHeight;
  }
  return Math.ceil(baseCutHeight / repeatIn) * repeatIn;
};

const applyHorizontalRepeatToFabricRules = (fabricRules = {}, repeat = {}) => {
  const fabricWidthIn = Math.max(1, nonNegativeNumber(fabricRules.fabricWidthIn, 1));
  const baseUsableWidthIn = Math.max(
    1,
    nonNegativeNumber(
      fabricRules.usableFabricWidthIn,
      fabricWidthIn - nonNegativeNumber(fabricRules.usableWidthDeductionIn, 0)
    )
  );
  const repeatIn = nonNegativeNumber(repeat.horizontalRepeatInch, 0);
  if (!repeat.horizontalEnabled || repeatIn <= 0 || baseUsableWidthIn <= 0) {
    return {
      ...fabricRules,
      horizontalRepeatAdjusted: false,
      repeatSettings: repeat,
    };
  }

  const repeatAlignedUsableWidth = Math.floor(baseUsableWidthIn / repeatIn) * repeatIn;
  const adjustedUsableWidthIn =
    repeatAlignedUsableWidth > 0
      ? Math.min(baseUsableWidthIn, repeatAlignedUsableWidth)
      : baseUsableWidthIn;

  return {
    ...fabricRules,
    usableFabricWidthIn: round2(adjustedUsableWidthIn),
    usableWidthDeductionIn: round2(Math.max(0, fabricWidthIn - adjustedUsableWidthIn)),
    horizontalRepeatAdjusted: adjustedUsableWidthIn < baseUsableWidthIn,
    baseUsableFabricWidthIn: round2(baseUsableWidthIn),
    horizontalRepeatInch: repeatIn,
    repeatSettings: repeat,
  };
};

export const calculateProductRow = (
  row = {},
  productConfig,
  quoteSettings = {}
) => {
  const config = normalizeProductConfig(productConfig || {});
  const isAccessoryLine = isAccessoryCalculationType(config.calculationType);
  const widthIn = nonNegativeNumber(row.width, 0);
  const heightIn = nonNegativeNumber(row.height, 0);
  const discountPercent = Math.min(100, nonNegativeNumber(row.discount, 0));
  const ratePerSqft =
    config.sqftRules.rateSource === "default_rate"
      ? nonNegativeNumber(config.sqftRules.defaultRate, 0)
      : nonNegativeNumber(row.sqftRate || row.ratePerSqft, config.sqftRules.defaultRate);
  const isBlindSqftProduct = isBlindSqftCalculationProduct(config);
  const minBillableSqft = nonNegativeNumber(config.sqftRules.minSqft, 0);
  const rawSqft = round2((widthIn * heightIn) / 144);
  const rowBlindSqftMode =
    row?.sqftCalculation?.roundingMode ||
    row?.calculationSnapshot?.sqftCalculation?.roundingMode;
  const hasStoredBlindSqftValues =
    Number(row?.billableSqft ?? row?.adjSqft ?? 0) > 0 ||
    Number(row?.sqftAmount ?? 0) > 0 ||
    Number(row?.rowTotal ?? row?.total ?? 0) > 0;
  const blindSqftRoundingMode = normalizeBlindSqftRoundingMode(
    rowBlindSqftMode ||
      (quoteSettings?.preserveLegacyBlindSqftRounding &&
      hasStoredBlindSqftValues
        ? "ceil"
        : resolveBlindSqftRoundingMode(quoteSettings, "exact")),
    "exact"
  );
  const blindSqftCalculation = calculateBlindSqft({
    widthIn,
    heightIn,
    roundingMode: blindSqftRoundingMode,
  });
  const preMinimumBillableSqft = isBlindSqftProduct
    ? blindSqftCalculation.billableSqft
    : roundByRule(rawSqft, config.sqftRules.rounding);
  const billableSqft = isBlindSqftProduct
    ? Math.max(preMinimumBillableSqft, minBillableSqft)
    : roundByRule(Math.max(rawSqft, minBillableSqft), config.sqftRules.rounding);
  const sqftCalculation = {
    rawSqft: isBlindSqftProduct ? blindSqftCalculation.rawSqft : rawSqft,
    billableSqft: round2(billableSqft),
    roundingMode: isBlindSqftProduct
      ? blindSqftCalculation.roundingMode
      : config.sqftRules.rounding,
    minimumSqft: minBillableSqft,
    minimumApplied: minBillableSqft > 0 && preMinimumBillableSqft < minBillableSqft,
  };
  const sqftCharge =
    ["sqft_only", "sqft_plus_fabric"].includes(config.calculationType)
      ? round2(billableSqft * ratePerSqft)
      : 0;

  const stitchingRates = quoteSettings.stitchingRates || quoteSettings.stitchingStyles || {};
  const selectedStitchingStyle = resolveSelectedStitchingStyle(row, stitchingRates);
  const stitchCfg = selectedStitchingStyle?.config || {};
  const fabricEnabled =
    config.fabricRules.enabled &&
    ["fabric_meter", "sqft_plus_fabric"].includes(config.calculationType);
  let panels = 0;
  let cutHeightIn = 0;
  let fabricMeters = 0;
  let fullnessResult = null;
  let curtainPieceCalc = null;
  let fabricRulesForCalculation = null;
  let repeatSettingsForCalculation = {};
  if (fabricEnabled) {
    const fabricRules = resolveFabricRulesForRow(
      row,
      config,
      quoteSettings.fabricCalcSettings || {}
    );
    repeatSettingsForCalculation = resolveProductRepeatSettings(row, quoteSettings);
    fabricRulesForCalculation = applyHorizontalRepeatToFabricRules(
      fabricRules,
      repeatSettingsForCalculation
    );
    const dropAllowanceIn = nonNegativeNumber(fabricRulesForCalculation.dropAllowanceIn, 0);
    fullnessResult = resolveCurtainFullness(row, config, stitchingRates, {
      ...quoteSettings,
      fabricRules: fabricRulesForCalculation,
    });
    const { fullness } = fullnessResult;

    if (config.fabricRules.meterFormulaType === "simple_length") {
      panels = 1;
      cutHeightIn = applyVerticalRepeatToCutHeight(
        heightIn + dropAllowanceIn,
        repeatSettingsForCalculation
      );
      fabricMeters = round2(cutHeightIn / 39.37);
    } else if (config.fabricRules.meterFormulaType === "roman_panels") {
      curtainPieceCalc = calculateCurtainPieces({
        widthIn,
        fabricWidthIn: fabricRulesForCalculation.fabricWidthIn,
        usableWidthDeductionIn: fabricRulesForCalculation.usableWidthDeductionIn,
        fullness: 1,
      });
      panels = curtainPieceCalc.pieces;
      cutHeightIn = applyVerticalRepeatToCutHeight(
        heightIn + dropAllowanceIn,
        repeatSettingsForCalculation
      );
      fabricMeters = round2((panels * cutHeightIn) / 39.37);
    } else {
      curtainPieceCalc = calculateCurtainPieces({
        widthIn,
        fabricWidthIn: fabricRulesForCalculation.fabricWidthIn,
        usableWidthDeductionIn: fabricRulesForCalculation.usableWidthDeductionIn,
        fullness,
      });
      panels = curtainPieceCalc.pieces;
      cutHeightIn = applyVerticalRepeatToCutHeight(
        heightIn + dropAllowanceIn,
        repeatSettingsForCalculation
      );
      fabricMeters = round2((panels * cutHeightIn) / 39.37);
    }

    if (IS_DEV_UI_ENABLED && config.calculationType === "fabric_meter") {
      console.log("CURTAIN_PIECE_CALC_TRACE", {
        item: row.item || row.name || "",
        product: row.productType || row.productName || row.product || config.name,
        widthIn,
        fullness,
        fabricWidthIn: curtainPieceCalc?.fabricWidthIn,
        usableWidthDeductionIn: curtainPieceCalc?.usableWidthDeductionIn,
        usableFabricWidthIn: curtainPieceCalc?.usableFabricWidthIn,
        rawPieces: curtainPieceCalc?.rawPieces,
        pieces: curtainPieceCalc?.pieces,
        roundingMode: "ceil",
        calculationVersion: CURTAIN_USABLE_WIDTH_CEIL_CALCULATION_VERSION,
      });
    }
  }

  const mrpPerMeter = nonNegativeNumber(row.mrp || row.mrpPerMeter, 0);
  const fabricGrossAmount = fabricEnabled
    ? round2(fabricMeters * mrpPerMeter)
    : 0;

  const stitchingRate = nonNegativeNumber(
    row.customStitchRate || stitchCfg.rate,
    0
  );
  const stitchingCost =
    config.stitchingRules.enabled && widthIn > 0
      ? round2((widthIn / 12) * stitchingRate)
      : 0;

  const trackCost =
    config.trackRules.enabled && config.trackRules.allowTrack
      ? round2(
          nonNegativeNumber(stitchCfg.trackRateFt, 0) *
            nonNegativeNumber(stitchCfg.trackLengthFt, config.trackRules.defaultTrackLengthFt)
        )
      : 0;

  const feet =
    config.runningFeetRules.formula === "manual_feet" || config.calculationType === "running_length_rate"
      ? nonNegativeNumber(row.length ?? row.runningFeet ?? row.feet, 0)
      : round2(widthIn / 12);
  const runningFeetRate = nonNegativeNumber(
    row.ratePerLength ?? row.ratePerFeet ?? row.runningFeetRate ?? row.rate,
    config.runningFeetRules.defaultRate
  );
  const runningFeetCharge =
    config.calculationType === "running_length_rate"
      ? round2(feet * runningFeetRate)
      : 0;

  const quantity = Math.max(0, nonNegativeNumber(row.qty ?? row.quantity, 0));
  const ratePerPiece = nonNegativeNumber(
    row.unitRate ?? row.ratePerPiece ?? row.pieceRate ?? row.rate,
    config.quantityRules.defaultRate
  );
  const quantityCharge =
    config.calculationType === "quantity_rate"
      ? round2(quantity * ratePerPiece)
      : 0;

  const manualAmount =
    config.calculationType === "manual_amount"
      ? round2(nonNegativeNumber(row.manualAmount || row.amount || row.lineAmount, 0))
      : 0;

  const productCharge =
    config.calculationType === "sqft_only"
      ? sqftCharge
      : config.calculationType === "running_length_rate"
      ? runningFeetCharge
      : config.calculationType === "quantity_rate"
      ? quantityCharge
      : config.calculationType === "manual_amount"
      ? manualAmount
      : sqftCharge;
  const baseLineAmountBeforeDiscount = round2(
    productCharge + fabricGrossAmount + stitchingCost + trackCost
  );
  const discountScope = getRowDiscountScope(row, config, {
    hasFabricMeterComponent: fabricEnabled,
    fabricGrossAmount,
  });
  const discountBase =
    discountScope === "fabric_only"
      ? fabricGrossAmount
      : discountScope === "line_total"
      ? baseLineAmountBeforeDiscount
      : 0;
  const discountAmount = round2(discountBase * (discountPercent / 100));
  const fabricDiscountAmount =
    discountScope === "fabric_only" ? discountAmount : 0;
  const fabricCost =
    discountScope === "fabric_only"
      ? round2(Math.max(0, fabricGrossAmount - fabricDiscountAmount))
      : fabricGrossAmount;
  const lineTotal =
    discountScope === "line_total"
      ? round2(Math.max(0, baseLineAmountBeforeDiscount - discountAmount))
      : round2(productCharge + fabricCost + stitchingCost + trackCost);
  const rawGstRate = row.gstRate ?? row.gst;
  const resolvedGstRate =
    rawGstRate === "" || rawGstRate === null || rawGstRate === undefined
      ? quoteSettings.gstRate
      : rawGstRate;
  const gstRate = nonNegativeNumber(resolvedGstRate, 0);
  const gstAmount = round2((lineTotal * gstRate) / 100);
  const totalWithGst = round2(lineTotal + gstAmount);

  return {
    productId: config.id,
    productName: config.name,
    lineType: isAccessoryLine ? "accessory" : "measurement",
    calculationType: config.calculationType,
    widthIn,
    heightIn,
    rawSqft: sqftCalculation.rawSqft,
    billableSqft: round2(billableSqft),
    adjSqft: round2(billableSqft),
    sqftCalculation,
    fabricMeters,
    fabricMetersFull: fabricMeters,
    fabricMetersHalf: fabricMeters,
    stitchingCost,
    trackCost,
    productCharge,
    sqftAmount: sqftCharge,
    fabricCost,
    fabricGrossAmount,
    fabricDiscountPercent: discountPercent,
    fabricDiscountAmount,
    fabricNetAmount: fabricCost,
    baseLineAmountBeforeDiscount,
    discountScope,
    discountBase,
    rowSubtotalBeforeGst: lineTotal,
    lineTotal,
    rowTotal: lineTotal,
    totalPieces: config.calculationType === "quantity_rate" ? quantity : panels || (isAccessoryLine ? 0 : 1),
    pcs: isAccessoryLine ? quantity || 1 : panels || quantity || 1,
    pcsFull: isAccessoryLine ? quantity || 1 : panels || quantity || 1,
    pcsHalf: isAccessoryLine ? quantity || 1 : panels || quantity || 1,
    cutCurtain: cutHeightIn,
    cutRoman: cutHeightIn,
    repeatSettings: repeatSettingsForCalculation,
    disableStitching: !config.stitchingRules.enabled,
    isCurtain: !isAccessoryLine && config.calculationType === "fabric_meter",
    isRoman: !isAccessoryLine && config.fabricRules.meterFormulaType === "roman_panels",
    isBlind: !isAccessoryLine && config.category === "blind",
    isAccessory: isAccessoryLine,
    quantity,
    unitRate: ratePerPiece,
    runningLength: feet,
    ratePerLength: runningFeetRate,
    lengthUnit: row.lengthUnit || config.runningFeetRules.defaultUnit || "ft",
    unit: row.unit || config.quantityRules.defaultUnit || "pcs",
    discountAmount,
    taxableAmount: lineTotal,
    gstRate,
    gstAmount,
    total: totalWithGst,
    productionQuantity:
      config.calculationType === "running_length_rate"
        ? feet
        : config.calculationType === "quantity_rate"
        ? quantity
        : config.calculationType === "manual_amount"
        ? 1
        : panels || 1,
    displayQuantity:
      config.calculationType === "running_length_rate"
        ? feet
        : config.calculationType === "quantity_rate"
        ? quantity
        : config.calculationType === "manual_amount"
        ? 1
        : panels || 1,
    shouldEnterFabricSummary:
      !isAccessoryLine && config.workshopRules.includeFabricSummary === true,
    shouldEnterWorkshop: config.workshopRules.includeInWorkshopPdf !== false,
    calculationBreakdown: {
      calculationType: config.calculationType,
      formulaLabel: PRODUCT_CALCULATION_LABELS[config.calculationType],
      appliedMinSqft: config.sqftRules.minSqft,
      sqftCalculation,
      sqftRounding: sqftCalculation.roundingMode,
      panels,
      cutHeightIn,
      fabricSource: fabricEnabled ? "product_config" : "none",
      rateSource: config.sqftRules.rateSource,
      feet,
      quantity,
      runningLength: feet,
      ratePerLength: runningFeetRate,
      unitRate: ratePerPiece,
      fullness: fullnessResult,
      curtainPieceCalc,
      fabricRules: fabricEnabled ? fabricRulesForCalculation : null,
      repeatSettings: repeatSettingsForCalculation,
    },
    workshopData: {
      includeInWorkshopPdf: config.workshopRules.includeInWorkshopPdf,
      includeFabricSummary: !isAccessoryLine && config.workshopRules.includeFabricSummary,
      includeRollOptimizer: !isAccessoryLine && config.workshopRules.includeRollOptimizer,
      fabricMeters: isAccessoryLine ? 0 : fabricMeters,
      panels,
      cutHeightIn,
    },
  };
};
